import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Slider,
  Box,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLeverage, setLeverage as setLeverageApi } from '@/apiServices';
import { useToast } from '@/shared/context/ToastProvider';
import { useMaxLeverage } from '@/context/MaxLeverageContext';

const whiteSliderStyles = {
  '& .MuiSlider-thumb': {
    color: 'white',
    transition: 'none',
    width: 14,
    height: 14,
    '&:hover, &.Mui-focusVisible': {
      boxShadow: `0px 0px 0px 8px ${alpha('#ffffff', 0.16)}`,
    },
    '&.Mui-active': {
      boxShadow: `0px 0px 0px 14px ${alpha('#ffffff', 0.16)}`,
    },
  },
  '& .MuiSlider-track': {
    color: 'white',
    transition: 'none',
  },
  '& .MuiSlider-rail': {
    color: 'white',
    transition: 'none',
  },
  '& .MuiSlider-mark': {
    color: 'white',
    transition: 'none',
  },
  '& .MuiSlider-markLabel': {
    color: 'white',
    transition: 'none',
  },
  '& .MuiSlider-valueLabel': {
    color: 'white',
    transition: 'none',
  },
};

function LeverageChip({ leverage, exchange, pairId, adjustLeverage, maxLeverage }) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAdjustLeverage = async (newLeverage) => {
    await adjustLeverage(newLeverage);
  };

  // Show 1x as default when leverage is not available
  const displayLeverage = leverage || 1;
  const isDefault = !leverage;

  return (
    <Tooltip title={`Leverage: ${displayLeverage}x${isDefault ? ' (default)' : ''}`}>
      <Chip
        disabled={isDefault}
        label={`${displayLeverage}x`}
        size='small'
        sx={{
          opacity: isDefault ? 0.6 : 1,
        }}
        variant='outlined'
        onClick={() => {
          handleOpen();
        }}
      />
      {open && (
        <AdjustLeverageModal
          exchange={exchange}
          handleAdjustLeverage={handleAdjustLeverage}
          handleClose={handleClose}
          leverage={displayLeverage}
          maxLeverage={maxLeverage}
          open={open}
          pairId={pairId}
        />
      )}
    </Tooltip>
  );
}

function AdjustLeverageModal({ leverage, exchange, pairId, open, handleClose, handleAdjustLeverage, maxLeverage }) {
  const [value, setValue] = useState(leverage);
  const [isAdjusting, setIsAdjusting] = useState(false);

  const { showToastMessage } = useToast();

  const marks = useMemo(() => {
    return [1, 5, 10, 20, 50, 100]
      .filter((val) => val <= maxLeverage)
      .map((val) => ({
        value: val,
        label: `${val}x`,
      }));
  }, [maxLeverage]);

  const handleAdjustLeverageCallback = async () => {
    setIsAdjusting(true);
    try {
      await handleAdjustLeverage(value);
      showToastMessage({ message: `Leverage adjusted to ${value}x`, type: 'success' });
    } catch (error) {
      showToastMessage({ message: `Failed to adjust leverage: ${error.message}`, type: 'error' });
    } finally {
      setIsAdjusting(false);
    }

    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent>
        <Stack alignItems='center' direction='column' spacing={2}>
          <Stack alignItems='center' direction='column' spacing={1}>
            <Typography variant='h6'>Adjust Leverage</Typography>
            <Typography variant='body1'>
              For {pairId}@{exchange}
            </Typography>
          </Stack>

          <Stack alignItems='center' direction='column' spacing={8} sx={{ width: 500 }}>
            <Slider
              disabled={isAdjusting}
              marks={marks}
              max={maxLeverage}
              min={1}
              step={1}
              sx={whiteSliderStyles}
              value={value}
              valueLabelDisplay='auto'
              onChange={(_, newValue) => setValue(newValue)}
            />

            <Button
              fullWidth
              color='success'
              disabled={isAdjusting}
              variant='contained'
              onClick={() => handleAdjustLeverageCallback(value)}
            >
              {isAdjusting ? <CircularProgress size={20} /> : `Confirm ${value}x`}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function useLeverage({ pair, account }) {
  const [leverage, setLeverage] = useState(null);
  const [maxLeverage, setMaxLeverage] = useState(100); // Default to 100x max leverage

  const { getMaxLeverage } = useMaxLeverage();

  useEffect(() => {
    const fetchLeverage = async () => {
      setLeverage(null);
      setMaxLeverage(100); // Reset to default max leverage
      try {
        const response = await getLeverage(pair.id, [account.id]);
        setLeverage(response);
        setMaxLeverage(getMaxLeverage(pair.id, account.exchangeName) || 100);
      } catch (e) {
        // noop, just don't show leverage
      }
    };

    if (pair && pair.market_type !== 'spot' && account) {
      fetchLeverage();
    } else {
      // When account is not set, provide default values
      setLeverage(null);
      setMaxLeverage(100);
    }
  }, [pair, account, getMaxLeverage]);

  const adjustLeverage = async (newLeverage) => {
    if (!account) {
      // Can't adjust leverage without an account
      return;
    }
    await setLeverageApi(newLeverage, pair.id, [account.id]);
    setLeverage((prev) => ({ ...prev, leverage: newLeverage }));
  };

  return { leverage, adjustLeverage, maxLeverage };
}

export { LeverageChip, useLeverage };
